import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { useLanguage } from '../../contexts/LanguageContext';
import '../../styles/global.css';

interface ProductionLine {
  id: number;
  name: string;
}

interface ImportedFile {
  id: number;
  original_filename: string;
  items_count: number;
  created_at: string;
  importer?: {
    name: string;
  };
}

const AssignProduction: React.FC = () => {
  const { t } = useLanguage();
  const [lines, setLines] = useState<ProductionLine[]>([]);
  const [importedFiles, setImportedFiles] = useState<ImportedFile[]>([]);
  const [selectedLine, setSelectedLine] = useState('');
  const [week, setWeek] = useState('');
  const [selectedFileId, setSelectedFileId] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);

  useEffect(() => {
    fetchLines();
    fetchImportedFiles();
  }, []);

  const fetchLines = async () => {
    try {
      const response = await api.get('/production-lines');
      setLines(response.data);
    } catch (error) {
      console.error('Error fetching production lines:', error);
    }
  };

  const fetchImportedFiles = async () => {
    try {
      const response = await api.get('/imported-files');
      setImportedFiles(response.data);
    } catch (error) {
      console.error('Error fetching imported files:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedLine || !week || !selectedFileId) {
      setMessage(t('assignments.fillAllFields'));
      setIsSuccess(false);
      return;
    }

    setLoading(true);
    setMessage('');

    try {
      await api.post('/production-assignments', {
        production_line_id: selectedLine,
        week: week,
        imported_file_id: selectedFileId,
      });
      setMessage(t('assignProduction.assignmentSuccess'));
      setIsSuccess(true);
      setSelectedLine('');
      setWeek('');
      setSelectedFileId('');
    } catch (error: any) {
      setMessage(error.response?.data?.message || t('assignProduction.assignmentFailed'));
      setIsSuccess(false);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pl-PL', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const selectedFile = importedFiles.find((f) => f.id.toString() === selectedFileId);

  return (
    <div>
      <div className="page-header" style={{ padding: '8px 0', marginBottom: 20 }}>
        <h1
          style={{
            color: '#2d3748',
            background: 'none',
            WebkitBackgroundClip: 'initial',
            WebkitTextFillColor: '#2d3748',
            backgroundClip: 'initial',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
          }}
        >
          <span style={{ WebkitTextFillColor: 'initial' }}>🔗</span>
          {t('assignProduction.title')}
        </h1>
      </div>

      <div className="card" style={{ maxWidth: 720 }}>
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: 20 }}>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: 500, color: '#4a5568' }}>
              {t('assignments.line')} *
            </label>
            <select
              value={selectedLine}
              onChange={(e) => setSelectedLine(e.target.value)}
              required
              className="input"
            >
              <option value="">{t('assignments.selectLine')}</option>
              {lines.map((line) => (
                <option key={line.id} value={line.id}>
                  {line.name}
                </option>
              ))}
            </select>
          </div>

          <div style={{ marginBottom: 20 }}>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: 500, color: '#4a5568' }}>
              {t('assignments.weekLabel')} *
            </label>
            <input
              type="text"
              value={week}
              onChange={(e) => setWeek(e.target.value)}
              placeholder="2024-01"
              required
              className="input"
            />
          </div>

          <div style={{ marginBottom: 20 }}>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: 500, color: '#4a5568' }}>
              {t('assignProduction.selectImportedFile')} *
            </label>
            <select
              value={selectedFileId}
              onChange={(e) => setSelectedFileId(e.target.value)}
              required
              className="input"
            >
              <option value="">{t('assignments.selectFile')}</option>
              {importedFiles.map((file) => (
                <option key={file.id} value={file.id}>
                  {file.original_filename} ({file.items_count}) — {formatDate(file.created_at)}
                </option>
              ))}
            </select>
          </div>

          {selectedFile && (
            <div
              style={{
                marginBottom: 20,
                padding: '14px 16px',
                backgroundColor: '#eef2ff',
                border: '1px solid #c7d2fe',
                borderRadius: 8,
                color: '#2d3748',
              }}
            >
              <div style={{ marginBottom: 4 }}>
                <strong style={{ color: '#667eea' }}>{t('assignProduction.selectedFile')}:</strong> {selectedFile.original_filename}
              </div>
              <div style={{ marginBottom: 6 }}>
                <strong style={{ color: '#667eea' }}>{t('assignProduction.itemsCount')}:</strong> {selectedFile.items_count}
              </div>
              <small style={{ color: '#4a5568' }}>{t('assignProduction.assignmentHint')}</small>
            </div>
          )}

          {message && (
            <div
              style={{
                marginBottom: 20,
                padding: '12px 16px',
                backgroundColor: isSuccess ? '#d1fae5' : '#fee2e2',
                color: isSuccess ? '#065f46' : '#991b1b',
                borderRadius: 8,
                fontWeight: 500,
              }}
            >
              {message}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="btn"
            style={{
              padding: '12px 24px',
              fontSize: 15,
              backgroundColor: '#667eea',
              color: 'white',
              fontWeight: 600,
              opacity: loading ? 0.6 : 1,
              cursor: loading ? 'not-allowed' : 'pointer',
            }}
          >
            {loading ? t('assignProduction.assigning') : t('assignProduction.assign')}
          </button>
        </form>
      </div>
    </div>
  );
};

export default AssignProduction;
